<template>
  <div class="rbac-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.rbac.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.rbac.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="openCreateRoleModal">{{ $t('admin.rbac.btn_create_role') }}</button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <span class="spinner"></span>
      {{ $t('admin.rbac.loading') }}
    </div>

    <template v-else>
      <!-- Roles Card -->
      <div class="card">
        <div class="card-header">
          <h3>{{ $t('admin.rbac.card_roles') }}</h3>
          <button class="btn btn-primary btn-sm" @click="openCreateRoleModal">{{ $t('admin.rbac.btn_add_role') }}</button>
        </div>
        <div class="card-body">
          <table>
            <thead>
              <tr>
                <th>{{ $t('admin.rbac.col_role_name') }}</th>
                <th>{{ $t('admin.rbac.col_description') }}</th>
                <th>{{ $t('admin.rbac.col_users') }}</th>
                <th>{{ $t('admin.rbac.col_permissions') }}</th>
                <th>{{ $t('admin.rbac.col_actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="roles.length === 0">
                <td colspan="5" class="empty-row">{{ $t('admin.rbac.no_roles') }}</td>
              </tr>
              <tr v-for="role in roles" :key="role.id">
                <td class="role-name">{{ formatRoleName(role.name) }}</td>
                <td>{{ role.description || '—' }}</td>
                <td>
                  <span class="badge" :class="userCountBadgeClass(role.name)">
                    {{ getRoleUserCount(role.id) }}
                  </span>
                </td>
                <td>
                  <span v-if="role.name === 'super_admin'" class="badge badge-green">All</span>
                  <span v-else class="perm-count">{{ role.permissions.length }}/{{ allPermissions.length }}</span>
                </td>
                <td>
                  <div class="actions-cell">
                    <template v-if="role.name === 'super_admin'">
                      <button class="btn btn-secondary btn-sm" disabled>{{ $t('admin.rbac.badge_protected') }}</button>
                    </template>
                    <template v-else>
                      <button class="btn btn-secondary btn-sm" @click="openEditRoleModal(role)">{{ $t('admin.rbac.btn_edit') }}</button>
                      <button
                        v-if="!role.is_built_in"
                        class="btn btn-ghost btn-sm"
                        @click="confirmDeleteRole(role)"
                      >{{ $t('admin.rbac.btn_delete') }}</button>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Permission Matrix Card -->
      <div class="card">
        <div class="card-header">
          <h3>{{ $t('admin.rbac.card_permissions') }}</h3>
          <button
            class="btn btn-primary btn-sm"
            :disabled="!matrixDirty || savingMatrix"
            @click="savePermissionMatrix"
          >
            {{ savingMatrix ? $t('admin.rbac.btn_saving_permissions') : $t('admin.rbac.btn_save_permissions') }}
          </button>
        </div>
        <div class="card-body matrix-scroll">
          <table class="matrix-table">
            <thead>
              <tr>
                <th class="perm-name-col">Permission</th>
                <th v-for="role in editableRoles" :key="role.id" class="role-col">
                  {{ formatRoleName(role.name) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in permissionGroups" :key="group.name">
                <tr>
                  <td :colspan="editableRoles.length + 1" class="permission-group-header">
                    {{ group.name }}
                  </td>
                </tr>
                <tr v-for="perm in group.permissions" :key="perm.id">
                  <td>{{ formatPermissionName(perm.name) }}</td>
                  <td v-for="role in editableRoles" :key="role.id" class="checkbox-cell">
                    <input
                      v-if="role.name === 'super_admin'"
                      type="checkbox"
                      checked
                      disabled
                    />
                    <input
                      v-else
                      type="checkbox"
                      :checked="matrixState[role.id]?.has(perm.id)"
                      @change="toggleMatrixPermission(role.id, perm.id)"
                    />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Admin Users Card -->
      <div class="card">
        <div class="card-header">
          <h3>{{ $t('admin.rbac.card_users') }}</h3>
          <button class="btn btn-primary btn-sm" @click="openAddAdminModal">{{ $t('admin.rbac.btn_add_admin') }}</button>
        </div>
        <div class="card-body">
          <table>
            <thead>
              <tr>
                <th>{{ $t('admin.rbac.col_name') }}</th>
                <th>{{ $t('admin.rbac.col_email') }}</th>
                <th>{{ $t('admin.rbac.col_role') }}</th>
                <th>{{ $t('admin.rbac.col_status') }}</th>
                <th>{{ $t('admin.rbac.col_last_login') }}</th>
                <th>{{ $t('admin.rbac.col_actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="adminUsers.length === 0">
                <td colspan="6" class="empty-row">{{ $t('admin.rbac.no_admin_users') }}</td>
              </tr>
              <tr v-for="admin in adminUsers" :key="admin.id">
                <td class="admin-name">{{ admin.name }}</td>
                <td>{{ admin.email }}</td>
                <td>
                  <span class="badge" :class="roleBadgeClass(admin.role_name)">
                    {{ formatRoleName(admin.role_name || 'none') }}
                  </span>
                </td>
                <td>
                  <span class="badge" :class="admin.is_active ? 'badge-green' : 'badge-red'">
                    {{ admin.is_active ? $t('admin.rbac.badge_active') : $t('admin.rbac.badge_inactive') }}
                  </span>
                </td>
                <td class="mono-value">{{ formatDateTime(admin.last_login_at) }}</td>
                <td>
                  <div class="actions-cell">
                    <template v-if="admin.role_name === 'super_admin' && isSelf(admin.id)">
                      <button class="btn btn-secondary btn-sm" disabled>{{ $t('admin.rbac.badge_you') }}</button>
                    </template>
                    <template v-else>
                      <button class="btn btn-secondary btn-sm" @click="openEditAdminModal(admin)">{{ $t('admin.rbac.btn_edit') }}</button>
                      <button
                        v-if="!isSelf(admin.id)"
                        class="btn btn-ghost btn-sm"
                        @click="toggleAdminStatus(admin)"
                      >
                        {{ admin.is_active ? $t('admin.rbac.btn_disable') : $t('admin.rbac.btn_enable') }}
                      </button>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- Add/Edit Admin Modal -->
    <div v-if="adminModal.visible" class="modal-overlay" @click.self="closeAdminModal">
      <div class="modal">
        <div class="modal-header">
          <h3 class="modal-title">{{ adminModal.editId ? $t('admin.rbac.user_modal_title_edit') : $t('admin.rbac.user_modal_title_add') }}</h3>
          <span class="modal-close" @click="closeAdminModal">&times;</span>
        </div>
        <div class="modal-body">
          <div v-if="adminModal.error" class="modal-error">{{ adminModal.error }}</div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.rbac.field_full_name') }}</label>
            <input v-model="adminModal.name" type="text" placeholder="Enter full name" />
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.rbac.field_email') }}</label>
            <input v-model="adminModal.email" type="email" placeholder="admin@casino.com" />
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.rbac.field_role') }}</label>
            <select v-model="adminModal.role_id">
              <option value="">Select role...</option>
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ formatRoleName(role.name) }}
              </option>
            </select>
          </div>
          <div v-if="!adminModal.editId" class="form-group">
            <label class="form-label">{{ $t('admin.rbac.field_password') }}</label>
            <input v-model="adminModal.password" type="password" placeholder="Min 8 chars, mixed case, numbers" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeAdminModal">{{ $t('admin.rbac.btn_cancel') }}</button>
          <button class="btn btn-primary" :disabled="adminModal.saving" @click="saveAdmin">
            {{ adminModal.saving ? $t('admin.rbac.btn_saving') : adminModal.editId ? $t('admin.rbac.btn_update') : $t('admin.rbac.btn_create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Role Modal -->
    <div v-if="roleModal.visible" class="modal-overlay" @click.self="closeRoleModal">
      <div class="modal">
        <div class="modal-header">
          <h3 class="modal-title">{{ roleModal.editId ? $t('admin.rbac.role_modal_title_edit') : $t('admin.rbac.role_modal_title_create') }}</h3>
          <span class="modal-close" @click="closeRoleModal">&times;</span>
        </div>
        <div class="modal-body">
          <div v-if="roleModal.error" class="modal-error">{{ roleModal.error }}</div>
          <div v-if="!roleModal.editId" class="form-group">
            <label class="form-label">{{ $t('admin.rbac.field_role_name') }}</label>
            <input v-model="roleModal.name" type="text" placeholder="e.g., editor, operator" />
            <span class="form-hint">Lowercase letters, numbers, underscores only</span>
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.rbac.field_role_description') }}</label>
            <input v-model="roleModal.description" type="text" placeholder="Describe this role's purpose" />
          </div>
          <div class="form-group">
            <label class="form-label">Permissions</label>
            <div class="perm-checklist">
              <template v-for="group in permissionGroups" :key="group.name">
                <div class="perm-group-label">{{ group.name }}</div>
                <label v-for="perm in group.permissions" :key="perm.id" class="perm-check-item">
                  <input
                    type="checkbox"
                    :checked="roleModal.permission_ids.has(perm.id)"
                    @change="toggleRoleModalPermission(perm.id)"
                  />
                  <span>{{ formatPermissionName(perm.name) }}</span>
                </label>
              </template>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeRoleModal">{{ $t('admin.rbac.btn_cancel') }}</button>
          <button class="btn btn-primary" :disabled="roleModal.saving" @click="saveRole">
            {{ roleModal.saving ? $t('admin.rbac.btn_saving') : roleModal.editId ? $t('admin.rbac.btn_update') : $t('admin.rbac.btn_create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Role Confirmation Modal -->
    <div v-if="deleteModal.visible" class="modal-overlay" @click.self="closeDeleteModal">
      <div class="modal modal-sm">
        <div class="modal-header">
          <h3 class="modal-title">{{ $t('admin.rbac.delete_role_title') }}</h3>
          <span class="modal-close" @click="closeDeleteModal">&times;</span>
        </div>
        <div class="modal-body">
          <p>{{ $t('admin.rbac.delete_role_text', { name: formatRoleName(deleteModal.roleName) }) }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeDeleteModal">{{ $t('admin.rbac.btn_cancel') }}</button>
          <button class="btn btn-danger" :disabled="deleteModal.saving" @click="deleteRole">
            {{ deleteModal.saving ? $t('admin.rbac.btn_deleting') : $t('admin.rbac.btn_confirm_delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useAdminStore } from '@/stores/admin'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const adminStore = useAdminStore()
const toast = useToastStore()

// ----------- Types -----------

interface PermissionItem {
  id: number
  name: string
  description: string | null
}

interface RoleItem {
  id: number
  name: string
  description: string | null
  is_built_in: boolean
  permissions: PermissionItem[]
  created_at: string
}

interface AdminUserItem {
  id: number
  name: string
  email: string
  role_name: string | null
  last_login_at: string | null
  is_active: boolean
}

// ----------- State -----------

const loading = ref(true)
const roles = ref<RoleItem[]>([])
const allPermissions = ref<PermissionItem[]>([])
const adminUsers = ref<AdminUserItem[]>([])

// Permission matrix state: role_id -> Set<permission_id>
const matrixState = ref<Record<number, Set<number>>>({})
const matrixOriginal = ref<Record<number, Set<number>>>({})
const matrixDirty = ref(false)
const savingMatrix = ref(false)

// ----------- Computed -----------

const editableRoles = computed(() => roles.value)

const permissionGroups = computed(() => {
  const groups: { name: string; permissions: PermissionItem[] }[] = []
  const groupMap = new Map<string, PermissionItem[]>()

  for (const perm of allPermissions.value) {
    const groupName = categorizePermission(perm.name)
    if (!groupMap.has(groupName)) {
      groupMap.set(groupName, [])
    }
    groupMap.get(groupName)!.push(perm)
  }

  for (const [name, permissions] of groupMap) {
    groups.push({ name, permissions })
  }

  return groups
})

// ----------- Data Fetching -----------

async function fetchData(): Promise<void> {
  loading.value = true
  try {
    const [rolesRes, adminsRes] = await Promise.all([
      apiClient.get('/admin/api/roles'),
      apiClient.get('/admin/api/admin-users'),
    ])

    roles.value = rolesRes.data.data
    allPermissions.value = rolesRes.data.all_permissions
    adminUsers.value = adminsRes.data.data

    initMatrixState()
  } catch {
    toast.error(t('admin.rbac.error_load'))
  } finally {
    loading.value = false
  }
}

function initMatrixState(): void {
  const state: Record<number, Set<number>> = {}
  const original: Record<number, Set<number>> = {}

  for (const role of roles.value) {
    const permIds = new Set(role.permissions.map((p) => p.id))
    state[role.id] = new Set(permIds)
    original[role.id] = new Set(permIds)
  }

  matrixState.value = state
  matrixOriginal.value = original
  matrixDirty.value = false
}

function toggleMatrixPermission(roleId: number, permId: number): void {
  const rolePerms = matrixState.value[roleId]
  if (!rolePerms) return

  if (rolePerms.has(permId)) {
    rolePerms.delete(permId)
  } else {
    rolePerms.add(permId)
  }

  // Check if matrix differs from original
  checkMatrixDirty()
}

function checkMatrixDirty(): void {
  for (const role of roles.value) {
    if (role.name === 'super_admin') continue
    const current = matrixState.value[role.id]
    const original = matrixOriginal.value[role.id]
    if (!current || !original) continue

    if (current.size !== original.size) {
      matrixDirty.value = true
      return
    }
    for (const id of current) {
      if (!original.has(id)) {
        matrixDirty.value = true
        return
      }
    }
  }
  matrixDirty.value = false
}

async function savePermissionMatrix(): Promise<void> {
  savingMatrix.value = true
  try {
    const updates: Promise<unknown>[] = []

    for (const role of roles.value) {
      if (role.name === 'super_admin') continue
      const current = matrixState.value[role.id]
      const original = matrixOriginal.value[role.id]
      if (!current || !original) continue

      // Check if this role changed
      let changed = current.size !== original.size
      if (!changed) {
        for (const id of current) {
          if (!original.has(id)) {
            changed = true
            break
          }
        }
      }

      if (changed) {
        updates.push(
          apiClient.put(`/admin/api/roles/${role.id}`, {
            permission_ids: Array.from(current),
          }),
        )
      }
    }

    await Promise.all(updates)
    toast.success(t('admin.rbac.success_permissions_saved'))

    // Refresh data to get updated state
    await fetchData()
  } catch {
    toast.error(t('admin.rbac.error_permissions_save'))
  } finally {
    savingMatrix.value = false
  }
}

// ----------- Admin User Modal -----------

const adminModal = reactive({
  visible: false,
  editId: null as number | null,
  name: '',
  email: '',
  role_id: '' as string | number,
  password: '',
  saving: false,
  error: '',
})

function openAddAdminModal(): void {
  adminModal.visible = true
  adminModal.editId = null
  adminModal.name = ''
  adminModal.email = ''
  adminModal.role_id = ''
  adminModal.password = ''
  adminModal.error = ''
  adminModal.saving = false
}

function openEditAdminModal(admin: AdminUserItem): void {
  adminModal.visible = true
  adminModal.editId = admin.id
  adminModal.name = admin.name
  adminModal.email = admin.email
  adminModal.role_id = findRoleIdByName(admin.role_name) ?? ''
  adminModal.password = ''
  adminModal.error = ''
  adminModal.saving = false
}

function closeAdminModal(): void {
  adminModal.visible = false
}

async function saveAdmin(): Promise<void> {
  adminModal.error = ''
  adminModal.saving = true

  try {
    if (adminModal.editId) {
      // Update
      const payload: Record<string, unknown> = {
        name: adminModal.name,
        email: adminModal.email,
        role_id: Number(adminModal.role_id),
      }
      await apiClient.put(`/admin/api/admin-users/${adminModal.editId}`, payload)
      toast.success(t('admin.rbac.success_user_updated'))
    } else {
      // Create
      if (!adminModal.name || !adminModal.email || !adminModal.role_id || !adminModal.password) {
        adminModal.error = t('admin.rbac.error_required')
        adminModal.saving = false
        return
      }
      await apiClient.post('/admin/api/admin-users', {
        name: adminModal.name,
        email: adminModal.email,
        role_id: Number(adminModal.role_id),
        password: adminModal.password,
      })
      toast.success(t('admin.rbac.success_user_created'))
    }

    closeAdminModal()
    await fetchData()
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } }
    adminModal.error = err.response?.data?.message ?? t('admin.rbac.error_user_save')
  } finally {
    adminModal.saving = false
  }
}

async function toggleAdminStatus(admin: AdminUserItem): Promise<void> {
  try {
    if (admin.is_active) {
      // Deactivate
      await apiClient.delete(`/admin/api/admin-users/${admin.id}`)
      toast.success(t('admin.rbac.success_user_disabled'))
    } else {
      // Reactivate
      await apiClient.put(`/admin/api/admin-users/${admin.id}`, { is_active: true })
      toast.success(t('admin.rbac.success_user_enabled'))
    }
    await fetchData()
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } }
    toast.error(err.response?.data?.message ?? t('admin.rbac.error_user_toggle'))
  }
}

// ----------- Role Modal -----------

const roleModal = reactive({
  visible: false,
  editId: null as number | null,
  name: '',
  description: '',
  permission_ids: new Set<number>(),
  saving: false,
  error: '',
})

function openCreateRoleModal(): void {
  roleModal.visible = true
  roleModal.editId = null
  roleModal.name = ''
  roleModal.description = ''
  roleModal.permission_ids = new Set()
  roleModal.error = ''
  roleModal.saving = false
}

function openEditRoleModal(role: RoleItem): void {
  roleModal.visible = true
  roleModal.editId = role.id
  roleModal.name = role.name
  roleModal.description = role.description ?? ''
  roleModal.permission_ids = new Set(role.permissions.map((p) => p.id))
  roleModal.error = ''
  roleModal.saving = false
}

function closeRoleModal(): void {
  roleModal.visible = false
}

function toggleRoleModalPermission(permId: number): void {
  if (roleModal.permission_ids.has(permId)) {
    roleModal.permission_ids.delete(permId)
  } else {
    roleModal.permission_ids.add(permId)
  }
}

async function saveRole(): Promise<void> {
  roleModal.error = ''
  roleModal.saving = true

  try {
    if (roleModal.editId) {
      // Update
      await apiClient.put(`/admin/api/roles/${roleModal.editId}`, {
        description: roleModal.description || null,
        permission_ids: Array.from(roleModal.permission_ids),
      })
      toast.success(t('admin.rbac.success_role_updated'))
    } else {
      // Create
      if (!roleModal.name) {
        roleModal.error = t('admin.rbac.error_role_name')
        roleModal.saving = false
        return
      }
      if (roleModal.permission_ids.size === 0) {
        roleModal.error = t('admin.rbac.error_role_permissions')
        roleModal.saving = false
        return
      }
      await apiClient.post('/admin/api/roles', {
        name: roleModal.name,
        description: roleModal.description || null,
        permission_ids: Array.from(roleModal.permission_ids),
      })
      toast.success(t('admin.rbac.success_role_created'))
    }

    closeRoleModal()
    await fetchData()
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } }
    roleModal.error = err.response?.data?.message ?? t('admin.rbac.error_role_save')
  } finally {
    roleModal.saving = false
  }
}

// ----------- Delete Role Modal -----------

const deleteModal = reactive({
  visible: false,
  roleId: null as number | null,
  roleName: '',
  saving: false,
})

function confirmDeleteRole(role: RoleItem): void {
  deleteModal.visible = true
  deleteModal.roleId = role.id
  deleteModal.roleName = role.name
  deleteModal.saving = false
}

function closeDeleteModal(): void {
  deleteModal.visible = false
}

async function deleteRole(): Promise<void> {
  if (!deleteModal.roleId) return
  deleteModal.saving = true

  try {
    await apiClient.delete(`/admin/api/roles/${deleteModal.roleId}`)
    toast.success(t('admin.rbac.success_role_deleted'))
    closeDeleteModal()
    await fetchData()
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } }
    toast.error(err.response?.data?.message ?? t('admin.rbac.error_role_delete'))
    closeDeleteModal()
  }
}

// ----------- Helpers -----------

function isSelf(adminId: number): boolean {
  return adminStore.user?.id === adminId
}

function findRoleIdByName(roleName: string | null): number | null {
  if (!roleName) return null
  const role = roles.value.find((r) => r.name === roleName)
  return role?.id ?? null
}

function getRoleUserCount(roleId: number): number {
  return adminUsers.value.filter((a) => {
    const rid = findRoleIdByName(a.role_name)
    return rid === roleId
  }).length
}

function formatRoleName(name: string): string {
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function formatPermissionName(name: string): string {
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function categorizePermission(name: string): string {
  if (name.includes('player')) return 'Players'
  if (name.includes('game')) return 'Games'
  if (name.includes('finance') || name.includes('withdrawal') || name.includes('credit') || name.includes('wallet')) return 'Finance'
  if (name.includes('bonus')) return 'Bonuses'
  if (name.includes('setting')) return 'Settings & System'
  if (name.includes('audit')) return 'Settings & System'
  if (name.includes('content')) return 'Content'
  if (name.includes('affiliate')) return 'Affiliates'
  if (name.includes('admin') || name.includes('role') || name.includes('rbac')) return 'Administration'
  return 'Other'
}

function roleBadgeClass(roleName: string | null): string {
  switch (roleName) {
    case 'super_admin': return 'badge-gold'
    case 'admin': return 'badge-blue'
    case 'moderator': return 'badge-purple'
    case 'support': return 'badge-orange'
    case 'finance_officer': return 'badge-gold'
    default: return 'badge-blue'
  }
}

function userCountBadgeClass(roleName: string): string {
  return roleBadgeClass(roleName)
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

// ----------- Lifecycle -----------

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.rbac-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-primary, #fff);
}

.btn-danger {
  background: var(--color-danger, #ed4245);
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background: #d63235;
}

.btn-sm {
  padding: 6px 10px;
  font-size: 12px;
}

/* Cards */
.card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.card-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.card-body {
  overflow-x: auto;
}

/* Tables */
table {
  width: 100%;
  border-collapse: collapse;
}

thead th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  background: var(--bg-tertiary, #213743);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  white-space: nowrap;
}

tbody td {
  padding: 12px 16px;
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

tbody tr:hover {
  background: rgba(255, 255, 255, 0.02);
}

tbody tr:last-child td {
  border-bottom: none;
}

.empty-row {
  text-align: center;
  color: var(--text-tertiary, #7f8fa4);
  padding: 48px 16px !important;
}

.role-name,
.admin-name {
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.perm-count {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

.mono-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 12px;
}

.actions-cell {
  display: flex;
  gap: 6px;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-green {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-red {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-blue {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

.badge-gold {
  background: rgba(255, 215, 0, 0.15);
  color: var(--accent-gold, #ffd700);
}

.badge-purple {
  background: rgba(155, 89, 182, 0.15);
  color: var(--accent-purple, #9b59b6);
}

.badge-orange {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

/* Permission Matrix */
.matrix-scroll {
  overflow-x: auto;
}

.matrix-table {
  min-width: 600px;
}

.perm-name-col {
  min-width: 200px;
}

.role-col {
  text-align: center;
  min-width: 100px;
}

.checkbox-cell {
  text-align: center;
}

.checkbox-cell input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--accent-primary, #00e701);
}

.permission-group-header {
  background: rgba(20, 117, 225, 0.08);
  padding: 10px 16px !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent-secondary, #1475e1) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

/* Loading */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 64px 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

.modal-sm {
  max-width: 400px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  font-size: 24px;
  color: var(--text-tertiary, #7f8fa4);
  cursor: pointer;
  transition: color 150ms ease;
  line-height: 1;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
}

.modal-body {
  padding: 20px;
}

.modal-body p {
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.modal-error {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
  padding: 10px 14px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  margin-bottom: 16px;
}

/* Form controls */
.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 6px;
}

.form-hint {
  display: block;
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 4px;
}

.modal input[type="text"],
.modal input[type="email"],
.modal input[type="password"],
.modal select {
  width: 100%;
  padding: 8px 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-sm, 4px);
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-family: inherit;
  transition: border-color 150ms ease;
}

.modal input:focus,
.modal select:focus {
  outline: none;
  border-color: var(--accent-secondary, #1475e1);
}

.modal input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.modal select option {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
}

/* Permission checklist in role modal */
.perm-checklist {
  max-height: 300px;
  overflow-y: auto;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-sm, 4px);
  padding: 8px;
}

.perm-group-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent-secondary, #1475e1);
  padding: 8px 8px 4px;
  margin-top: 4px;
}

.perm-group-label:first-child {
  margin-top: 0;
}

.perm-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  transition: background 150ms ease;
}

.perm-check-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.perm-check-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: var(--accent-primary, #00e701);
  cursor: pointer;
  flex-shrink: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .rbac-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  td,
  th {
    padding: 10px 12px;
  }
}
</style>
